import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpDown, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  bulkGenerateBpInvoices,
  fetchArSummary,
  fetchFirmInvoices,
  type FirmInvoiceRow,
} from "@/features/invoices/api";
import { InvoiceStatusBadge } from "@/features/invoices/components/invoice-status-badge";
import type { ArSummary, InvoiceStatus } from "@/features/invoices/types";
import { cn, formatCents } from "@/lib/utils";

type StatusFilter = "ALL" | InvoiceStatus;
type KindTab = "ALL" | "CAP" | "BP";
type SortKey = "invoiceNumber" | "clientName" | "invoiceDate" | "dueDate" | "totalCents" | "balanceCents" | "agingDays" | "status";

const KIND_TABS: { value: KindTab; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "CAP", label: "CAP Clients" },
  { value: "BP", label: "BP invoices" },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "ALL", label: "All statuses" },
  { value: "OPEN", label: "Open" },
  { value: "PAID", label: "Paid" },
  { value: "VOID", label: "Void" },
  { value: "DRAFT", label: "Draft" },
];

const AGING_CARDS: { key: keyof ArSummary; label: string }[] = [
  { key: "total", label: "Total AR" },
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1–30 days" },
  { key: "d31_60", label: "31–60 days" },
  { key: "d61_90", label: "61–90 days" },
  { key: "d90_plus", label: "90+ days" },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FirmInvoiceRow[]>([]);
  const [summary, setSummary] = useState<ArSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("ALL");
  const [kindTab, setKindTab] = useState<KindTab>("ALL");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "invoiceDate",
    dir: "desc",
  });

  const [generating, setGenerating] = useState(false);

  const reload = useCallback(async () => {
    const [invoiceRows, ar] = await Promise.all([fetchFirmInvoices(), fetchArSummary()]);
    setRows(invoiceRows);
    setSummary(ar);
  }, []);

  useEffect(() => {
    let active = true;
    reload()
      .catch(() => undefined)
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [reload]);

  async function handleBulkGenerate() {
    setGenerating(true);
    try {
      const created = await bulkGenerateBpInvoices();
      await reload();
      toast.success(
        created > 0
          ? `Generated ${created} BP draft ${created === 1 ? "invoice" : "invoices"}.`
          : "Every BP client already has a draft invoice.",
      );
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Couldn't generate invoices.");
    } finally {
      setGenerating(false);
    }
  }

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (kindTab !== "ALL" && (r.invoice.kind ?? "CAP") !== kindTab) return false;
      if (status !== "ALL" && r.invoice.status !== status) return false;
      if (overdueOnly && !(r.invoice.status === "OPEN" && r.agingDays > 0)) return false;
      if (term) {
        const hay = `${r.clientName} ${r.invoice.invoiceNumber}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });

    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }, [rows, search, status, kindTab, overdueOnly, sort]);

  function toggleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  }

  if (loading) return <Loader className="mt-10" />;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground font-heading mb-2 text-3xl font-semibold tracking-[-0.02em]">
          Invoices
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          All invoices across your clients, with accounts-receivable aging.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {AGING_CARDS.map((c) => (
          <Card key={c.key} className="p-4">
            <div className="text-muted-foreground text-xs tracking-wide uppercase">{c.label}</div>
            <div className={cn("mt-1 text-xl font-bold", c.key === "total" && "text-primary")}>
              {summary ? formatCents(summary[c.key]) : "—"}
            </div>
          </Card>
        ))}
      </div>

      {/* CAP vs BP books — kept visibly separate per QA so the two revenue
          streams don't blur together. BP invoices can be raised in bulk. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="bg-muted inline-flex flex-wrap gap-1 rounded-sm p-1">
          {KIND_TABS.map((t) => {
            const active = t.value === kindTab;
            return (
              <button
                key={t.value}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => setKindTab(t.value)}
                className={cn(
                  "inline-flex h-9 items-center rounded-[10px] px-4 text-[13px] font-medium transition-colors",
                  active
                    ? "bg-brand-panel font-semibold text-white"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {kindTab === "BP" && (
          <Button variant="outline" size="sm" disabled={generating} onClick={handleBulkGenerate}>
            <Plus className="size-4" />
            {generating ? "Generating…" : "Generate BP drafts"}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search client or invoice #"
          className="max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={overdueOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setOverdueOnly((v) => !v)}
        >
          Overdue only
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <SortHeader label="Invoice #" k="invoiceNumber" sort={sort} onSort={toggleSort} />
              <SortHeader label="Client" k="clientName" sort={sort} onSort={toggleSort} />
              <SortHeader label="Date" k="invoiceDate" sort={sort} onSort={toggleSort} />
              <SortHeader label="Due" k="dueDate" sort={sort} onSort={toggleSort} />
              <SortHeader label="Age" k="agingDays" sort={sort} onSort={toggleSort} />
              <SortHeader label="Status" k="status" sort={sort} onSort={toggleSort} />
              <SortHeader label="Amount" k="totalCents" sort={sort} onSort={toggleSort} align="right" />
              <SortHeader label="Balance" k="balanceCents" sort={sort} onSort={toggleSort} align="right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((r) => (
              <TableRow
                key={r.invoice.id}
                role="button"
                className="cursor-pointer"
                onClick={() => navigate(`/client/${r.invoice.clientId}/invoices/${r.invoice.id}`)}
              >
                <TableCell className="font-medium">{r.invoice.invoiceNumber}</TableCell>
                <TableCell>{r.clientName}</TableCell>
                <TableCell>{r.invoice.invoiceDate}</TableCell>
                <TableCell>{r.invoice.dueDate}</TableCell>
                <TableCell>
                  {r.invoice.status === "OPEN" && r.agingDays > 0 ? `${r.agingDays}d` : ""}
                </TableCell>
                <TableCell>
                  <InvoiceStatusBadge status={r.invoice.status} />
                </TableCell>
                <TableCell
                  className={cn("text-right", r.invoice.status === "VOID" && "text-muted-foreground line-through")}
                >
                  {formatCents(r.totalCents)}
                </TableCell>
                <TableCell className="text-right font-semibold">{formatCents(r.balanceCents)}</TableCell>
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground text-center">
                  No invoices match these filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function sortValue(row: FirmInvoiceRow, key: SortKey): string | number {
  switch (key) {
    case "clientName":
      return row.clientName.toLowerCase();
    case "invoiceNumber":
      return row.invoice.invoiceNumber;
    case "invoiceDate":
      return row.invoice.invoiceDate;
    case "dueDate":
      return row.invoice.dueDate;
    case "status":
      return row.invoice.status;
    case "totalCents":
      return row.totalCents;
    case "balanceCents":
      return row.balanceCents;
    case "agingDays":
      return row.agingDays;
  }
}

function SortHeader({
  label,
  k,
  sort,
  onSort,
  align,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: "asc" | "desc" };
  onSort: (k: SortKey) => void;
  align?: "right";
}) {
  return (
    <TableHead className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 font-medium",
          align === "right" && "flex-row-reverse",
          sort.key === k ? "text-foreground" : "text-muted-foreground",
        )}
        onClick={() => onSort(k)}
      >
        {label}
        <ArrowUpDown className="size-3" />
      </button>
    </TableHead>
  );
}
